import * as React from 'react'
import * as Redux from 'react-redux'
import { styled, css } from 'next-yak'

import * as consent from '@owl-nest/consent'
import { t, tc } from '@owl-nest/localize'
import * as model from '@owl-nest/models'
import {
  BREAKPOINTS,
  Callout,
  COLORS,
  glyphs,
  Link,
  LinkAsButton,
  styles as S,
  VideoLoader,
} from '@ulule/owl-kit-components/next'
import * as env from '@owl-nest/config'

import { DisplayMode } from '../../../../context'
import * as projectModel from '../../../../models/project'
import { type RootState } from '../../../../../reducers'
import * as UFE from '../../../../../UFE'

import { EDIT_MODE_LAPTOP_BREAKPOINT, MIDDLE_TABLET_LAPTOP_BREAKPOINT } from '../../constants'

import { CreateButton } from '../CreateButton'
import { EditButton } from '../EditButton'
import { AdviceModal } from '../AdviceModal'

export function PrimaryMedia(): React.ReactElement<unknown> {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)
  const media = useProjectMedia()
  const userConfig = env.useUserEnv()

  const displayMode = React.useContext(DisplayMode)
  const [isAdviceModalOpen, setAdviceModalOpen] = React.useState(false)
  const [isImageModalOpen, setImageModalOpen] = React.useState(false)
  const isEditMode = displayMode === 'edit'

  const projectName = model.project.name<projectModel.ExtraFields>(project)

  return (
    <PrimaryMediaView $isEditMode={isEditMode && !model.project.refused<projectModel.ExtraFields>(project)}>
      {isEditMode && media.src === undefined ? (
        <CreateWrapper $isEditMode={isEditMode && !model.project.refused<projectModel.ExtraFields>(project)}>
          <CreateButton
            center={true}
            createHandler={() => {
              document.location.href = `/projects/${project.id}/edit/layout/${userConfig.lang}/?image`
            }}
            cta={
              <LinkWrapper>
                <Link
                  onClick={() => {
                    setImageModalOpen(true)
                  }}
                  tinted
                  withIcon
                >
                  <glyphs.stroke.Bulb size={15} />
                  {t('How to prepare your main image')}
                </Link>
              </LinkWrapper>
            }
            title={
              <CreateTitle>
                <S.heading.L asDesktop="M">{t('Add an image or a gif')}</S.heading.L>
              </CreateTitle>
            }
          />
          {media.videoHtml ? (
            <EditButtonContainer>
              <EditButton link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?video`}>
                {t('Edit video')}
              </EditButton>
            </EditButtonContainer>
          ) : (
            <Callout type="info">
              {tc('[strong: Are you planning to make a video?] Even better! You can add it with your image.', {
                strong: <strong />,
              })}
              <S.copy.S.regular>
                <Link
                  onClick={() => {
                    setAdviceModalOpen(true)
                  }}
                  tinted
                  withIcon
                >
                  <glyphs.stroke.Bulb size={15} />
                  {t('How to create a beautiful video')}
                </Link>
              </S.copy.S.regular>
            </Callout>
          )}
        </CreateWrapper>
      ) : (
        <>
          {media.videoHtml ? (
            <StyledVideoLoader
              alt={t('Video presentation of the project')}
              consentWall={consent.media.Wall}
              placeholder={media.src}
              provider={media.provider}
              srcSet={media.srcSet}
              videoHtml={media.videoHtml}
              $isEditMode={isEditMode && !model.project.refused<projectModel.ExtraFields>(project)}
            />
          ) : (
            projectName && (
              <Image
                src={media.src}
                srcSet={media.srcSet}
                alt={t('Cover image of the project %(title)s', {
                  title: projectName,
                })}
              />
            )
          )}
          {isEditMode && !model.project.refused<projectModel.ExtraFields>(project) && getEditButtons()}
        </>
      )}
      <AdviceModal
        link={`/projects/${project.id}/edit/layout/?video`}
        open={isAdviceModalOpen}
        setOpen={setAdviceModalOpen}
        title={t('Add an image and/or a video')}
        videoHtml={
          userConfig.lang === 'fr'
            ? '<iframe allow="accelerometer; autoplay; encrypted-media;" allowFullScreen frameBorder="0" src="https://www.youtube.com/embed/oozewGzhFYY?autoplay=1" title="video-placeholder"/>'
            : '<iframe allow="accelerometer; autoplay; encrypted-media;" allowFullScreen frameBorder="0" src="https://www.youtube.com/embed/wQiYRkI3mm4?autoplay=1" title="video-placeholder" />'
        }
      />
      <AdviceModal
        ctaText={t('Add an image or a gif')}
        description={t(
          'It will be the first glimpse of your project and will allow to identify it in the lists and on the social networks. Image format is 680x380 and up to 2040x1140 for optimal display on all screens. Choose a simple and clear visual that illustrates your project well. You can even add a gif!',
        )}
        link={`/projects/${project.id}/edit/layout/?video`}
        open={isImageModalOpen}
        setOpen={setImageModalOpen}
        title={t('Add an image or a gif')}
        imageSrc={UFE.IMAGES.adviceMainImage}
      />
    </PrimaryMediaView>
  )

  function getEditButtons(): React.ReactNode {
    if (media.videoHtml === undefined && media.src) {
      return (
        <EditButtonContainer>
          <AdviceVideoEditButton>
            <EditButton
              fullWidth={true}
              glyph={<glyphs.stroke.Plus size={18} />}
              link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?video`}
            >
              {t('Add a video')}
            </EditButton>

            <AdviceVideo>
              <LinkAsButton
                darkened
                onClick={() => {
                  setAdviceModalOpen(true)
                }}
                tinted
                withIcon
              >
                <glyphs.stroke.Bulb size={15} />
                {t('How to create a beautiful video')}
              </LinkAsButton>
            </AdviceVideo>
          </AdviceVideoEditButton>

          <EditButton link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?image`}>
            {t('Edit image')}
          </EditButton>
        </EditButtonContainer>
      )
    } else if (media.videoHtml && media.src) {
      return (
        <EditButtonContainer>
          <EditButton link={`/projects/${project.id}/edit/layout/${userConfig.lang}/?video`}>
            {media.videoHtml && media.src ? t('Edit image or video') : t('Edit video')}
          </EditButton>
        </EditButtonContainer>
      )
    }
  }
}

function useProjectMedia(): { src?: string; srcSet?: string; videoHtml?: string; provider?: string } {
  const project = Redux.useSelector<RootState, projectModel.Project>((state) => state.application.project.data)

  const x2Image = model.project.mainImage<projectModel.ExtraFields>(project, '2x')
  const largeImage = model.project.mainImage<projectModel.ExtraFields>(project)
  const videoHtml = model.project.video<projectModel.ExtraFields>(project)?.html

  return {
    provider: model.project.video<projectModel.ExtraFields>(project)?.provider_name,
    src: largeImage,
    srcSet: `${x2Image} 2x, ${x2Image} 3x`,
    videoHtml,
  }
}

const AdviceVideoEditButton = styled.div`
  position: relative;
`

const AdviceVideo = styled(S.copy.S.regular)`
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  width: fit-content;
  bottom: 14px;
  white-space: nowrap;

  ${LinkAsButton} {
    display: none;
  }

  @media screen and ${EDIT_MODE_LAPTOP_BREAKPOINT} {
    float: right;
    position: absolute;
    top: 10px;
    right: 24px;
    left: auto;
    bottom: auto;
    transform: translateX(0);

    ${LinkAsButton} {
      display: block;
      padding: 0;

      ${glyphs.stroke.Bulb} {
        margin-top: -4px;
      }
    }
  }
`

const CreateTitle = styled.div`
  margin-bottom: 10px;
  text-align: center;

  @media screen and ${BREAKPOINTS.LAPTOP} {
    text-align: left;
  }
`

const createWrapperBreakpointStyle = css`
  padding: 0px;

  ${Callout} {
    position: absolute;
    bottom: 10px;
    left: 10px;
    margin-top: 0px;

    ${Link} {
      display: block;
    }

    ${glyphs.stroke.Bulb} {
      top: -2px;
      color: ${COLORS.PRIMARY_GREY_500};
      margin-right: 5px;
    }
  }
`

const CreateWrapper = styled.div<{ $isEditMode: boolean }>`
  align-items: center;
  background-color: ${COLORS.PRIMARY_BLUE_100};
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  height: 100%;
  justify-content: center;
  padding: 60px 8px 8px;
  position: relative;
  width: 100%;

  ${Callout} {
    text-align: center;
    margin-top: 14px;
    width: calc(100% - 20px);

    ${Link} {
      display: block;
    }

    ${glyphs.stroke.Bulb} {
      top: -2px;
      color: ${COLORS.PRIMARY_GREY_500};
      margin-right: 5px;
    }
  }

  ${Callout} {
    background-color: ${COLORS.PRIMARY_GREY_000}; // HACK: Irregular background color manipulation
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${createWrapperBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${createWrapperBreakpointStyle};
        }
      `
    }
  }}
`

const LinkWrapper = styled(S.copy.S.regular)`
  position: relative;
  top: -25%;

  @media screen and (min-width: 1322px) {
    position: initial;
    top: initial;
  }
`

const EditButtonContainer = styled.span`
  position: absolute;
  right: 10px;
  top: 0px;

  ${EditButton} {
    display: block;
    margin-top: 10px;
  }

  @media screen and ${BREAKPOINTS.TABLET} {
    ${EditButton} {
      margin-bottom: 10px;
    }
  }
`

const Image = styled.img`
  display: block;
  object-fit: cover;
  height: 100%;
  width: 100%;
`

const StyledVideoLoader = styled(VideoLoader)<{ $isEditMode: boolean }>`
  width: 100%;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          max-width: 680px;
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          max-width: 680px;
        }
      `
    }
  }}

  @media screen and ${BREAKPOINTS.LAPTOP_L} {
    width: 680px;
  }
`

const primaryMediaViewBreakpointStyle = css<{ $isEditMode: boolean }>`
  max-height: 380px;
  max-width: 680px;

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        height: calc((63vw - 264px) * 9 / 16);
        width: calc(63vw - 264px);
        margin-right: 22px;
      `
    } else {
      return css`
        height: calc(54vw * 9 / 16);
        width: 54vw;
        margin-right: 0;
      `
    }
  }}
`

const PrimaryMediaView = styled.div<{ $isEditMode: boolean }>`
  position: relative;
  width: 100%;

  ${Image} + ${EditButtonContainer} {
    position: absolute;
    bottom: 0px;
    left: 10px;

    ${AdviceVideoEditButton} {
      ${EditButton} {
        width: 100%;
      }
    }

    ${AdviceVideoEditButton} + ${EditButton} {
      float: right;
    }
  }

  ${({ $isEditMode }) => {
    if ($isEditMode) {
      return css`
        @media screen and ${BREAKPOINTS.LAPTOP} {
          ${primaryMediaViewBreakpointStyle};
        }
      `
    } else {
      return css`
        @media screen and ${MIDDLE_TABLET_LAPTOP_BREAKPOINT} {
          ${primaryMediaViewBreakpointStyle};
        }
      `
    }
  }}

  @media screen and ${BREAKPOINTS.LAPTOP_L} {
    margin-right: ${({ $isEditMode }) => ($isEditMode ? '32px' : '0')};
    height: 380px;
    width: 680px;
  }
`
